import React, { useCallback, useState } from "react";
import MessageInput from "./MessageInput";
import PredefinedPrompts from "../PredefinedPrompts";
import PromptModal from "../PromptModal";
import styles from "./chatwindow.module.css";
import MessageBubble from "./MessageBubble";
import LoadingSpinner from "../LoadingSpinner";
import ContentCard from "../ContentCard";
import { Agents } from "../../types/master";
import { useChatCore } from "../ChatCore";
import { v4 as uuid } from "uuid";
import { getAgentSubtitle } from "../../utils/helper";
import { useAppDispatch } from "../../store";
import { resetRetainedIds } from "../../store/userSlice";

interface ChatWindowProps {
  agentId?: string;
  chatWindowColor?: string;
  currentAgent?: Agents | undefined;
  templateBlobUrl?: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  agentId,
  chatWindowColor,
  currentAgent,
  templateBlobUrl,
}) => {
  const dispatch = useAppDispatch();
  const sessionId = uuid();
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [selectedTemplateFileName, setSelectedTemplateFileName] = useState<
    string | undefined
  >();

  // Use shared chat logic
  const {
    agent,
    currentChat,
    loading,
    hasMessages,
    showFullScreenSpinner,
    defaultPrompts,
    value,
    setValue,
    inputRef,
    handlePromptClick,
    isViewingHistory,
  } = useChatCore(sessionId, currentAgent, agentId);

  // Agent-specific title/description
  let agentTitle = `${agent?.agent_name || ""}`;
  const agentSubTitle = getAgentSubtitle(agentId);

  const onPromptClick = useCallback(
    (prompt: string, templateFileName?: string) => {
      // Store the template file name
      setSelectedTemplateFileName(templateFileName);
      // Open the modal first (except for agent ID 26)
      if (agentId !== "26") {
        setIsPromptModalOpen(true);
      }
      // Then handle the prompt click
      handlePromptClick(prompt);
    },
    [handlePromptClick, agentId],
  );

  const handleClosePromptModal = useCallback(() => {
    setIsPromptModalOpen(false);
  }, []);

  // Check if there are any agent responses in the chat
  const hasAgentResponse = currentChat.some((msg) => msg.sender === "agent");

  // Check if workflow has ended (only for HR Agent - ID 18)
  // Workflow ends when last message is email_drafting AND contains draft_emails (final step)
  console.log("Testing currentChat", currentChat)
  const lastAgentMessage = [...currentChat]
    .reverse()
    .find((msg) => msg.sender === "agent");

  // Check if SOW workflow is completed
  let isSowCompleted = false;
  if (lastAgentMessage?.agentIdName === "sow_auto_filler") {
    try {
      const parsedMessage = JSON.parse(lastAgentMessage.message);
      const finalAnswer = parsedMessage?.finalAnswer || parsedMessage;
      isSowCompleted = finalAnswer?.status === "completed";
    } catch (e) {
      // If parsing fails, keep isSowCompleted as false
    }
  }

  const isWorkflowEnded =
    (agentId === "18" &&
      !loading &&
      lastAgentMessage?.agentIdName === "email_drafting" &&
      lastAgentMessage?.message?.includes("draft_emails")) ||
    lastAgentMessage?.agentIdName == "jd_details_agent" ||
    isSowCompleted;

  return (
    <>
      <div
        className={styles.chatWindowContainer}
      // style={chatWindowColor ? { background: chatWindowColor } : {}}
      >
        {showFullScreenSpinner ? (
          <div className={styles.chatWindow}>
            <LoadingSpinner size="lg" label="Loading session..." fullscreen />
          </div>
        ) : hasMessages ? (
          <>
            <MessageBubble
              currentChat={currentChat}
              agentAvatar="/images/g42-logo.png"
              showThinkingBubble={loading}
              inputRef={inputRef}
            />
          </>
        ) : (
          <>
            <div className={styles.chatHeader}>
              <ContentCard
                title={agentTitle}
                imageUrl="/images/g42-logo.png"
                subTitle={agentSubTitle}
              />
            </div>
            <PredefinedPrompts
              isSmallChatWindow={false}
              prompts={defaultPrompts}
              onPromptClick={onPromptClick}
            />
          </>
        )}
      </div>
      <div className={styles.inputContainer}>
        {/* History Mode Banner */}
        {isViewingHistory && (
          <div className={styles.historyBanner}>
            <div className={styles.historyBannerContent}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>You are viewing a historical conversation</span>
            </div>
            <button
              className={styles.historyBannerButton}
              onClick={() => {
                // Clear history mode using Redux action
                dispatch(resetRetainedIds());
              }}
            >
              Start New Conversation
            </button>
          </div>
        )}

        <MessageInput
          ref={inputRef}
          agentId={agentId}
          currentAgent={agent}
          value={value}
          setValue={setValue}
          showGenerateImageToggle={agentId === "11" || agentId === "106"}
          disableImageToggle={agentId === "11" || agentId === "106" || hasAgentResponse}
          showUploadProfilesToggle={agentId === "18"}
          disableUploadProfilesToggle={hasAgentResponse}
          disabled={isWorkflowEnded || isViewingHistory}
        />
      </div>

      {/* Prompt Template Modal */}
      <PromptModal
        open={isPromptModalOpen}
        onClose={handleClosePromptModal}
        blobUrl={agent?.template_url || templateBlobUrl}
        templateFileName={selectedTemplateFileName}
        anchored={false}
      />
    </>
  );
};

export default ChatWindow;
